import { useCallback, useEffect, useState } from "react";
import { getJson, type Reply } from "../api";

// Lecturas y título de las pantallas de Inteligencia artificial.

/** Título de la pestaña del navegador. */
export function useTitle(title: string) {
  useEffect(() => {
    document.title = `${title} - Bheli Admin`;
  }, [title]);
}

/**
 * Lectura de una pantalla: la primera respuesta, si no existe (404) y si falló, con «Reintentar». `reload` vuelve a
 * leer sin borrar lo que se ve (después de guardar).
 */
type AiResult<T> = { url: string; data: T | null; state: "ready" | "missing" | "failed"; error: Reply<unknown> | null };

/** Resultado de una lectura; si falla, conserva lo que ya se veía de la misma dirección. */
function toResult<T>(url: string, reply: Reply<T>, previous: AiResult<T> | null): AiResult<T> {
  if (reply.status === 200 && reply.body) return { url, data: reply.body, state: "ready", error: null };
  return {
    url,
    data: previous?.url === url ? previous.data : null,
    state: reply.status === 404 ? "missing" : "failed",
    error: reply as Reply<unknown>,
  };
}

export function useAiData<T>(url: string | null) {
  const [result, setResult] = useState<AiResult<T> | null>(null);
  const reload = useCallback(async () => {
    if (!url) return;
    const reply = await getJson<T>(url);
    setResult((previous) => toResult(url, reply, previous));
  }, [url]);
  // La lectura cambia el estado solo al llegar la respuesta; mientras la última respuesta sea de otra dirección, carga.
  useEffect(() => {
    if (!url) return;
    let live = true;
    void getJson<T>(url).then((reply) => live && setResult((previous) => toResult(url, reply, previous)));
    return () => {
      live = false;
    };
  }, [url]);
  const current = result && result.url === url ? result : null;
  return {
    data: current?.data ?? null,
    state: current?.state ?? ("loading" as const),
    error: current?.error ?? null,
    reload,
    retry: () => (setResult(null), void reload()),
  };
}
